/**
 * useRecordingStorage
 * IndexedDB-wrapper voor het opslaan van niet-verzonden opnames.
 * Opnames worden bewaard als ArrayBuffer (Blob is niet betrouwbaar in Safari).
 */

const DB_NAME = 'we-gesprekken-db';
const DB_VERSION = 1;
const STORE_NAME = 'pending-recordings';

export interface PendingRecording {
  id: string;
  timestamp: number;
  audioData: ArrayBuffer;
  mimeType: string;
  inwoner: string;
  datumGesprek: string;
  typeGesprek: string;
  duration: number;
  dossierId?: string;
}

// Singleton DB-promise — wordt één keer geopend en daarna hergebruikt
let dbPromise: Promise<IDBDatabase> | null = null;

function getDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (e) => {
        const db = (e.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('by-timestamp', 'timestamp');
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        dbPromise = null; // Reset zodat een volgende poging opnieuw probeert
        reject(request.error);
      };
    });
  }
  return dbPromise;
}

function idbTransaction<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return getDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const store = tx.objectStore(STORE_NAME);
        const req = action(store);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      })
  );
}

export const useRecordingStorage = () => {
  /**
   * Sla een opname op in IndexedDB. Retourneert het gegenereerde id.
   */
  const savePendingRecording = async (
    record: Omit<PendingRecording, 'id'>
  ): Promise<string> => {
    const id = crypto.randomUUID();
    const fullRecord: PendingRecording = { id, ...record };
    await idbTransaction('readwrite', (store) => store.put(fullRecord));
    console.log('💾 Opname opgeslagen in IndexedDB, id:', id);
    return id;
  };

  /**
   * Verwijder een opname uit IndexedDB (na succesvolle upload).
   */
  const deletePendingRecording = async (id: string): Promise<void> => {
    await idbTransaction('readwrite', (store) => store.delete(id));
    console.log('🗑️ Opname verwijderd uit IndexedDB, id:', id);
  };

  /**
   * Haal de meest recente niet-verzonden opname op (op timestamp aflopend).
   * Retourneert null als er geen opname is.
   */
  const loadPendingRecording = (): Promise<PendingRecording | null> => {
    return getDb().then(
      (db) =>
        new Promise<PendingRecording | null>((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, 'readonly');
          const index = tx.objectStore(STORE_NAME).index('by-timestamp');
          const req = index.openCursor(null, 'prev'); // meest recente eerst
          req.onsuccess = () => {
            const cursor = req.result;
            resolve(cursor ? (cursor.value as PendingRecording) : null);
          };
          req.onerror = () => reject(req.error);
        })
    );
  };

  return { savePendingRecording, deletePendingRecording, loadPendingRecording };
};
